import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, message } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../../stores/authStore';

const { TextArea } = Input;

type VoiceStatus = 'idle' | 'connecting' | 'listening' | 'stopping' | 'error';

interface Props {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}

async function float32ToPcm16k(input: Float32Array, inputRate: number) {
  const ratio = inputRate / 16000;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const pcm = new Int16Array(length);
  for (let i = 0; i < length; i += 1) {
    const sample = input[Math.floor(i * ratio)] || 0;
    const clipped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  return pcm;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

export default function VoiceNoteInput({ value, onChange, placeholder }: Props) {
  const authUser = useAuthStore(state => state.user);
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [liveText, setLiveText] = useState('');
  const [errorText, setErrorText] = useState('');
  const socketRef = useRef<Socket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const baseValueRef = useRef('');
  const finalTextRef = useRef('');
  const partialTextRef = useRef('');
  const liveTextRef = useRef('');
  const committedRef = useRef(false);
  const onChangeRef = useRef(onChange);

  const isRecording = status === 'connecting' || status === 'listening' || status === 'stopping';

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const cleanupAudio = useCallback(() => {
    try { processorRef.current?.disconnect(); } catch {}
    processorRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }, []);

  const commitLiveText = useCallback(() => {
    if (committedRef.current) return;
    const spoken = (liveTextRef.current || '').trim();
    if (!spoken) return;
    committedRef.current = true;
    const base = (baseValueRef.current || '').trim();
    onChangeRef.current(base ? `${base}\n${spoken}` : spoken);
  }, []);

  const stop = useCallback(() => {
    if (!socketRef.current && !streamRef.current) {
      setStatus('idle');
      return;
    }
    setStatus('stopping');
    commitLiveText();
    cleanupAudio();
    try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
    socketRef.current?.disconnect();
    socketRef.current = null;
    setStatus('idle');
    setLiveText('');
  }, [cleanupAudio, commitLiveText]);

  const start = useCallback(async () => {
    if (!authUser?.id) {
      setErrorText('请先登录后再录音');
      message.warning('请先登录后再录音');
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      const msg = '当前浏览器不支持麦克风或当前访问方式不是安全上下文，请使用 HTTPS 域名访问';
      setErrorText(msg);
      message.error(msg);
      setStatus('error');
      return;
    }

    setStatus('connecting');
    setErrorText('');
    setLiveText('');
    finalTextRef.current = '';
    partialTextRef.current = '';
    liveTextRef.current = '';
    baseValueRef.current = value || '';
    committedRef.current = false;

    try {
      const socket = io('/ws', { auth: { token: useAuthStore.getState().token } });
      socketRef.current = socket;

      socket.on('ai-demand:asr-ready', () => setStatus('listening'));
      socket.on('ai-demand:asr-text', (payload: { text?: string; isFinal?: boolean }) => {
        const text = payload?.text?.trim();
        if (!text) return;
        if (payload.isFinal) {
          if (!finalTextRef.current.endsWith(text)) {
            finalTextRef.current = `${finalTextRef.current ? `${finalTextRef.current}\n` : ''}${text}`;
          }
          partialTextRef.current = '';
        } else {
          partialTextRef.current = text;
        }
        const current = `${finalTextRef.current}${finalTextRef.current && partialTextRef.current ? '\n' : ''}${partialTextRef.current}`.trim();
        liveTextRef.current = current;
        setLiveText(current);
      });
      socket.on('ai-demand:asr-error', (payload: { message?: string }) => {
        const msg = payload?.message || '实时转写失败';
        setErrorText(msg);
        setStatus('error');
        message.error(msg);
      });
      socket.on('connect_error', (err: any) => {
        const msg = err?.message || '转写服务连接失败';
        setErrorText(msg);
        setStatus('error');
        message.error(msg);
      });
      socket.on('disconnect', () => {
        if (status === 'connecting' || status === 'listening') setStatus('idle');
      });

      socket.emit('ai-demand:asr-start');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
      streamRef.current = stream;
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') await audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = async event => {
        const socketNow = socketRef.current;
        if (!socketNow?.connected) return;
        const pcm = await float32ToPcm16k(event.inputBuffer.getChannelData(0), audioContext.sampleRate);
        socketNow.emit('ai-demand:asr-audio', { audio: arrayBufferToBase64(pcm.buffer) });
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (error: any) {
      cleanupAudio();
      try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      socketRef.current?.disconnect();
      socketRef.current = null;
      const msg = error?.name === 'NotAllowedError'
        ? '麦克风权限被拒绝，请在浏览器地址栏允许麦克风访问'
        : (error?.message || '无法启动语音输入');
      setErrorText(msg);
      setStatus('error');
      message.error(msg);
    }
  }, [authUser?.id, cleanupAudio, status, value]);

  useEffect(() => {
    return () => {
      commitLiveText();
      cleanupAudio();
      try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [cleanupAudio, commitLiveText]);

  const displayValue = isRecording && liveText
    ? `${value || ''}${value ? '\n' : ''}${liveText}`
    : (value || '');

  const hint = status === 'connecting'
    ? '正在连接转写服务…'
    : status === 'listening'
      ? (liveText || '正在聆听，说话后这里会实时显示文字')
      : status === 'stopping'
        ? '正在停止并写入输入框…'
        : errorText;

  return (
    <div className={`adc-voice-note-input ${isRecording ? 'adc-voice-note-listening' : ''} ${status === 'error' ? 'adc-voice-note-error' : ''}`}>
      <TextArea
        size="small"
        placeholder={placeholder || '补充需求或描述（可选）'}
        value={displayValue}
        onChange={e => {
          if (isRecording) return;
          onChange(e.target.value);
        }}
        autoSize={{ minRows: 2, maxRows: 5 }}
      />
      <button
        type="button"
        className="adc-voice-note-btn"
        onMouseDown={e => e.preventDefault()}
        onClick={isRecording ? stop : start}
        title={isRecording ? '点击停止录音并写入输入框' : '点击开始语音输入'}
      >
        <AudioOutlined spin={isRecording} />
      </button>
      {hint ? (
        <div className="adc-voice-note-live">
          <strong>{status === 'error' ? '语音输入异常' : isRecording ? '正在聆听' : '提示'}</strong>
          <span>{hint}</span>
        </div>
      ) : null}
    </div>
  );
}
